import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, FileText, Users, Check, AlertCircle, Clock, LogOut, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StaffDashboard() {
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();

  const [uploadForm, setUploadForm] = useState({
    client_id: "",
    project_name: "",
    drawing_name: "",
    description: "",
    file: null
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (!token || !userData) {
      navigate("/portal/login");
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (!["staff", "admin"].includes(parsedUser.user_type)) {
      navigate("/portal/login");
      return;
    }
    
    setUser(parsedUser);
    fetchData(token);
  }, [navigate]);

  const fetchData = async (token) => {
    try {
      const [clientsRes, drawingsRes] = await Promise.all([
        axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/drawings`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setClients(clientsRes.data);
      setDrawings(drawingsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/portal/login");
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setUploadForm({ ...uploadForm, file: e.target.files[0] });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.client_id) {
      toast.error("Please select a client and file");
      return;
    }

    const token = localStorage.getItem("token");
    setIsUploading(true);

    const formData = new FormData();
    formData.append("client_id", uploadForm.client_id);
    formData.append("project_name", uploadForm.project_name);
    formData.append("drawing_name", uploadForm.drawing_name);
    formData.append("description", uploadForm.description);
    formData.append("file", uploadForm.file);

    try {
      await axios.post(`${API}/drawings`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Drawing uploaded successfully! Client will be notified.");
      setShowUploadForm(false);
      setUploadForm({ client_id: "", project_name: "", drawing_name: "", description: "", file: null });
      fetchData(token);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      revision_required: "bg-red-100 text-red-800"
    };
    const icons = {
      pending: Clock,
      approved: Check,
      revision_required: AlertCircle
    };
    const Icon = icons[status] || Clock;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3" />
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#F8FAFC]" data-testid="staff-dashboard">
      {/* Header */}
      <div className="bg-[#0F172A] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Staff Dashboard</h1>
            <p className="text-[#94A3B8]">Production Drawing Management</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setShowUploadForm(true)}
              className="bg-[#F97316] hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload Drawing
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-[#0F172A]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-[#E2E8F0] p-6">
            <Users className="w-8 h-8 text-[#F97316] mb-2" />
            <div className="text-3xl font-bold text-[#0F172A]">{clients.length}</div>
            <div className="text-sm text-[#64748B]">Registered Clients</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-6">
            <FileText className="w-8 h-8 text-blue-500 mb-2" />
            <div className="text-3xl font-bold text-[#0F172A]">{drawings.length}</div>
            <div className="text-sm text-[#64748B]">Total Drawings</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-6">
            <Clock className="w-8 h-8 text-yellow-500 mb-2" />
            <div className="text-3xl font-bold text-yellow-600">
              {drawings.filter(d => d.status === "pending").length}
            </div>
            <div className="text-sm text-[#64748B]">Pending Approval</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] p-6">
            <Check className="w-8 h-8 text-green-500 mb-2" />
            <div className="text-3xl font-bold text-green-600">
              {drawings.filter(d => d.status === "approved").length}
            </div>
            <div className="text-sm text-[#64748B]">Approved</div>
          </div>
        </div>

        {/* Drawings Table */}
        <div className="bg-white border border-[#E2E8F0]">
          <div className="p-6 border-b border-[#E2E8F0]">
            <h2 className="text-xl font-bold text-[#0F172A]">All Drawings</h2>
          </div>
          
          {drawings.length === 0 ? (
            <div className="p-12 text-center">
              <Upload className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#64748B]">No drawings uploaded yet</p>
              <Button
                onClick={() => setShowUploadForm(true)}
                className="mt-4 bg-[#F97316] hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload First Drawing
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase">Drawing</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {drawings.map((drawing) => {
                    const client = clients.find(c => c.id === drawing.client_id);
                    return (
                      <tr key={drawing.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-[#64748B]" />
                            <span className="font-medium text-[#0F172A]">{drawing.drawing_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[#64748B]">{drawing.project_name}</td>
                        <td className="px-6 py-4 text-[#64748B]">{client?.company_name || "Unknown"}</td>
                        <td className="px-6 py-4">{getStatusBadge(drawing.status)}</td>
                        <td className="px-6 py-4 text-sm text-[#64748B]">
                          {new Date(drawing.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white w-full max-w-lg"
          >
            <div className="bg-[#0F172A] text-white p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold">Upload Production Drawing</h2>
              <button
                onClick={() => setShowUploadForm(false)}
                className="p-2 hover:bg-white/10 rounded-full"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Select Client *
                </label>
                <Select onValueChange={(value) => setUploadForm({ ...uploadForm, client_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.company_name} - {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Project Name *
                </label>
                <Input
                  required
                  value={uploadForm.project_name}
                  onChange={(e) => setUploadForm({ ...uploadForm, project_name: e.target.value })}
                  placeholder="e.g., TechCorp Mumbai Office"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Drawing Name *
                </label>
                <Input
                  required
                  value={uploadForm.drawing_name}
                  onChange={(e) => setUploadForm({ ...uploadForm, drawing_name: e.target.value })}
                  placeholder="e.g., Workstation Layout - Floor 3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Description
                </label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Additional details about the drawing..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">
                  Drawing File *
                </label>
                <Input
                  type="file"
                  required
                  accept=".pdf,.dwg,.dxf,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  className="cursor-pointer"
                />
                <p className="text-xs text-[#94A3B8] mt-1">
                  Supported: PDF, DWG, DXF, PNG, JPG
                </p>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowUploadForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isUploading}
                  className="flex-1 bg-[#F97316] hover:bg-orange-600"
                >
                  {isUploading ? "Uploading..." : "Upload & Notify Client"}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
